import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Spinner variants using CVA
 */
export const spinnerVariants = cva(
  'animate-spin rounded-full border-2 border-current border-t-transparent',
  {
    variants: {
      size: {
        xs: 'h-3 w-3',
        sm: 'h-4 w-4',
        md: 'h-6 w-6',
        lg: 'h-8 w-8',
        xl: 'h-12 w-12',
      },
      color: {
        default: 'text-gray-400',
        primary: 'text-primary-600 dark:text-primary-400',
        secondary: 'text-accent-600 dark:text-accent-400',
        white: 'text-white',
        current: 'text-current',
      },
    },
    defaultVariants: {
      size: 'md',
      color: 'primary',
    },
  }
);

/**
 * Spinner Component
 *
 * @param {Object} props
 * @param {'xs'|'sm'|'md'|'lg'|'xl'} [props.size='md']
 * @param {'default'|'primary'|'secondary'|'white'|'current'} [props.color='primary']
 * @param {string} [props.className]
 */
const Spinner = React.forwardRef(({ className, size, color, ...props }, ref) => {
  return (
    <div
      ref={ref}
      role="status"
      aria-label="Loading"
      className={cn(spinnerVariants({ size, color }), className)}
      {...props}
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
});

Spinner.displayName = 'Spinner';

/**
 * LoadingOverlay Component - Full overlay with centered spinner
 */
const LoadingOverlay = React.forwardRef(({ className, spinnerSize = 'lg', ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 z-10',
        className
      )}
      {...props}
    >
      <Spinner size={spinnerSize} />
    </div>
  );
});

LoadingOverlay.displayName = 'LoadingOverlay';

/**
 * Skeleton Component - For loading placeholders
 */
export const skeletonVariants = cva(
  'animate-pulse rounded bg-gray-200 dark:bg-gray-700',
  {
    variants: {
      variant: {
        text: 'h-4 w-full',
        title: 'h-6 w-3/4',
        avatar: 'h-10 w-10 rounded-full',
        card: 'h-32 w-full rounded-xl',
        button: 'h-10 w-24 rounded-lg',
        image: 'h-48 w-full rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'text',
    },
  }
);

const Skeleton = React.forwardRef(({ className, variant, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(skeletonVariants({ variant }), className)}
      {...props}
    />
  );
});

Skeleton.displayName = 'Skeleton';

export { Spinner, LoadingOverlay, Skeleton };
export default Spinner;
